import React from "react";
import { ChevronRight } from "lucide-react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  SkeletonCard,
  useEndReached,
} from "../../../components/ui";
import { RowTag, tintFor } from "./ClientsTable";
import { CLIENT_STATUS_STYLES } from "../statusStyles";
import { ClientNews, newsFor } from "../clientNews";
import { clientsNewsSince } from "../openedClients";
import { useOpenedClients } from "../hooks/useOpenedClients";
import { useWaitingSubmissions } from "../hooks/useWaitingSubmissions";
import type { Client } from "../types/clients.types";

/** The height the cards stand in at while the roster is on its way — about
 *  four cards, which is what a phone shows before the fold. */
const CARDS_SKELETON = "h-[380px]";

interface ClientCardsProps {
  clients: Client[];
  /** Stands in for the cards when the list comes back with none. */
  empty: EmptyStateProps;
  /** Where the list is — the same contract `ClientsTable` works to. */
  phase?: LoadPhase;
  /** Off for archived clients, who have no plan running to have a length. */
  showActivity?: boolean;
  onSelectClient: (client: Client) => void;
  /** Whether the caller is holding rows past the ones it has handed over. */
  hasMore?: boolean;
  /** Asks for the next batch — see `useEndReached`. */
  onEndReached?: () => void;
}

/**
 * The roster as it reads on a phone: one card per client instead of a table,
 * and no card around the lot of them.
 *
 * The table needs 760px to lay its five columns out. On a phone that is either
 * clipped at the right edge or scrolled sideways inside a card that is itself
 * inside a page — and a roster is the one list a practitioner opens this app to
 * read, so it is the last thing that should arrive sideways. The row becomes a
 * card carrying what somebody scanning their own client list is after: who they
 * are, what they are working towards, how long they have been on it, and
 * whether there is anything to do about them.
 *
 * Every column is on the face of the card, so there is no sheet behind it —
 * unlike the payments list, whose row has nine fields and no screen of its
 * own. Pressing a card opens the client, which is what the table's rows do too.
 *
 * Which of the two renders is `MyClientsPage`'s decision — see `useIsMobile`.
 */
const ClientCards: React.FC<ClientCardsProps> = ({
  clients,
  empty,
  phase = "ready",
  showActivity = true,
  onSelectClient,
  hasMore = false,
  onEndReached,
}) => {
  // The same three reads the table makes, for the same reason: what this
  // browser has already been shown, the line it started counting from, and the
  // answers that have come back since. Read once for the list rather than per
  // card — see `clientNews`.
  const seen = useOpenedClients();
  const since = clientsNewsSince();
  const waiting = useWaitingSubmissions();

  const { ref, onScroll } = useEndReached<HTMLDivElement>(hasMore, onEndReached);

  if (phase !== "ready" && clients.length === 0) {
    return (
      <div className="flex-1 min-h-0">
        <SkeletonCard phase={phase} className={CARDS_SKELETON} />
      </div>
    );
  }

  if (clients.length === 0) {
    // A column rather than a row, so the state gets the full width to set its
    // lines in — centred as a row it would be sized by its longest word and
    // could push past a phone's edge. It brings its own padding.
    return (
      <div className="flex-1 min-h-0 flex flex-col justify-center">
        <EmptyState {...empty} />
      </div>
    );
  }

  return (
    <div
      ref={ref}
      onScroll={onScroll}
      className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-overlay"
    >
      <ul className="flex flex-col gap-3 pb-4">
        {clients.map((client) => {
          const news: ClientNews = newsFor(client, seen, since, waiting);

          return (
            <li key={client.id}>
              <button
                type="button"
                onClick={() => onSelectClient(client)}
                // `active:` rather than `hover:` — a phone has no pointer to
                // hover with, and the press is what wants acknowledging.
                className="w-full card-surface border border-line bg-white p-4 text-left transition-colors active:bg-[#F8FEFF] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center ${tintFor(
                      client.name
                    )}`}
                  >
                    <span className="text-[15px] font-medium">
                      {client.name.charAt(0)}
                    </span>
                  </span>

                  {/* `min-w-0` is what lets a long name shorten instead of
                      pushing the status pill off the card's edge. */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate text-[15px] font-medium text-ink">
                        {client.name}
                      </h3>
                      <span
                        className={`ml-auto flex-shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          CLIENT_STATUS_STYLES[client.status]
                        }`}
                      >
                        {client.status}
                      </span>
                    </div>
                    {/* Every goal on one line, cut off by the card's edge
                        rather than counted into a "+N" the way the table does
                        it: that pill opens its full list on hover, and there is
                        no hover here to open it on. */}
                    <p className="mt-0.5 truncate text-[13px] text-ink-muted">
                      {client.goals.length > 0 ? client.goals.join(", ") : "-"}
                    </p>
                  </div>

                  <ChevronRight
                    aria-hidden
                    className="mt-1 w-4 h-4 flex-shrink-0 text-ink-subtle"
                  />
                </div>

                {/* The dates, and the one piece of news the card carries. The
                    tag sits down here rather than beside the name: on a phone
                    the name has the pill opposite it already, and a third mark
                    on that line is what shortens the name to nothing. */}
                <div className="mt-3 flex items-center justify-between gap-3 border-t border-line pt-3">
                  <span className="min-w-0 truncate text-[12px] text-ink-subtle">
                    {/* An archived client has no plan running to have a length,
                        so that tab's cards drop it the way the table drops the
                        column rather than showing a dash. */}
                    {showActivity && `${client.activity} · `}Joined{" "}
                    {client.joinedOn}
                  </span>
                  <RowTag news={news} />
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ClientCards;
